<template>
  <div class="mb-16">
    <div class="mb-estacion__select-container mb-8">
      <h2 class="text-xs uppercase tracking-wider leading-none mb-4">
        Estación de {{ tipo }}
        <span class="sr-only" v-if="estacion?.name">{{
          `: ${estacion?.name}` || ''
        }}</span>
      </h2>

      <div class="flex items-center gap-4">
        <button
          type="button"
          class="flex py-4 text-left border-b border-gray-300 bg-transparent text-2xl leading-none items-center dark:border-gray-700 dark:text-gray-200"
          :data-testid="`cambiar-estacion-${tipo.toLowerCase()}`"
          aria-haspopup="listbox"
          :aria-expanded="state.show"
          :aria-controls="`estacion-listbox-${tipo.toLowerCase()}`"
          @click="state.show = !state.show"
        >
          <span
            v-if="!estacion?.name"
            class="block h-24 w-100 rounded-sm bg-white/20 dark:bg-white/10"
          />
          <span v-else class="mr-4 text-2xl leading-none not-sr-only">{{
            estacion?.name
          }}</span>
          <LucideChevronDown
            :size="24"
            :class="[
              'duration-200 ease-in-out stroke-current transition text-orange',
              state.show ? 'rotate-180' : '',
            ]"
          />
        </button>
        <NuxtLink
          :to="`/${$route.params.servicio}/mapa?estacion=${estacion?.slug}`"
          class="flex items-center"
          v-umami="{ name: 'detalle-estacion', data: estacion?.slug }"
        >
          <LucideInfo :size="16" class="w-16! h-16!" />
          <span class="sr-only"
            >Ver estacion {{ estacion?.name }} en el mapa</span
          >
        </NuxtLink>
      </div>
      <Transition name="slide-open">
        <ul
          :id="`estacion-listbox-${tipo.toLowerCase()}`"
          v-show="state.show"
          role="listbox"
          :class="[
            'bg-white dark:bg-gray-900 transition-all overflow-y-auto border border-gray-200 dark:border-gray-700 shadow absolute z-10 w-[calc(100vw-(var(--spacing)*16*2))] max-w-fit h-200',
          ]"
          :data-testid="`lista-estaciones-${tipo.toLowerCase()}`"
        >
          <li
            v-for="(ests, estI) in estaciones"
            hydrate-on-visible
            :key="ests._id"
            role="option"
            :aria-selected="ests._id === estacionId"
            class="flex items-center border-b border-gray-200 dark:border-gray-700"
            :style="{ '--i': estI }"
          >
            <span aria-hidden="true" class="p-8 text-left">
              <LucideMapPin
                :size="16"
                aria-hidden="true"
                class="fill-current stroke-current"
                :style="`color: ${ests.color}`"
              />
            </span>
            <button
              type="button"
              class="flex-1 p-8 text-left"
              :data-testid="`select-${tipo.toLowerCase()}-${ests._id}`"
              @click="selectEstacion(ests)"
            >
              {{ ests.name }}
            </button>
            <button
              type="button"
              :aria-label="
                ests.destacado
                  ? `Quitar ${ests.name} de favoritas`
                  : `Destacar estación ${ests.name}`
              "
              class="p-8 text-left"
              @click="starEstacion($event, ests)"
              v-umami="{
                name: 'estacion-save',
                data: `${ests.srv}>${ests._id}`,
              }"
              data-testid="btn-star"
            >
              <ClientOnly>
                <LucideStar
                  v-if="Boolean(ests.destacado)"
                  :size="16"
                  aria-hidden="true"
                  class="text-orange fill-current stroke-current"
                />
                <LucideStar
                  v-else
                  :size="16"
                  :stroke-width="1"
                  aria-hidden="true"
                  class="dark:text-white text-black"
                />
              </ClientOnly>
            </button>
          </li>
        </ul>
      </Transition>
    </div>
    <div v-if="estacionesUser?.length" class="flex flex-wrap gap-4">
      <button
        :class="['mb-btn', estacionUser?._id === estacionId ? 'activo' : '']"
        v-for="estacionUser of estacionesUserCompletas"
        :disabled="estacionUser?._id === estacionId"
        @click="selectEstacion(estacionUser!)"
      >
        {{ estacionUser?.name }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { IEstacion } from '~~/interfaces';

interface Props {
  tipo: string;
  estacionId: string | number;
  estacionesBase?: Map<string, IEstacion>;
  estacionesUser?: string[];
}

const {
  tipo,
  estacionId,
  estacionesBase = new Map<string, IEstacion>(),
  estacionesUser = [],
} = defineProps<Props>();

const estacionBase = computed(() => estacionesBase.get(String(estacionId)));
const estacionesArray = computed(() =>
  Array.from(estacionesBase.values()).reduce((init, srv) => {
    if (init.findIndex((s) => s._id === srv._id) >= 0) {
      return init;
    }
    return [...init, srv];
  }, [] as IEstacion[])
);

const estacion = ref(estacionBase.value || estacionesArray.value[0]);

const state = reactive({
  show: false,
});

const estaciones = computed<IEstaciones[]>(() => {
  const estacionesNormales: IEstaciones[] = [];
  const estacionesDestacadas: IEstaciones[] = [];
  const zonasLen = new Set<string>();
  const colors = new Set<string>();

  estacionesArray.value.forEach((est) => {
    if (estacionesUser.includes(String(est._id))) {
      const indexEstUser = estacionesUser.indexOf(String(est._id));
      estacionesDestacadas[indexEstUser] = Object.assign({}, est, {
        destacado: true,
      });
    } else {
      estacionesNormales.push(est);
    }
    if (!zonasLen.has(est.zona)) {
      zonasLen.add(est.zona);
    }
  });

  zonasLen.forEach((zona) => {
    colors.add(
      `oklch(0.8 0.5 ${Math.round(360 / zonasLen.size + 1) * parseInt(zona, 10)} / 1)`
    );
  });

  const newestaciones = [...estacionesDestacadas, ...estacionesNormales].map(
    (est) => {
      return Object.assign({}, est, {
        color: [...colors][parseInt(est.zona, 10)],
      });
    }
  );

  return newestaciones;
});

watch(
  () => estacionId,
  () => {
    const est = estaciones.value.find(
      (est: IEstaciones) => String(est._id) === String(estacionId)
    );
    estacion.value = est || estacion.value;
  },
  { deep: true, immediate: true }
);

type IEstaciones = IEstacion & { destacado?: boolean; color?: string };

const emit = defineEmits(['estacion', 'star']);

const starEstacion = (event: MouseEvent, est: IEstacion) => {
  event.stopPropagation();
  emit('star', est);
};

const estacionesUserCompletas = computed(() =>
  estacionesUser
    .map((estacionId) => estacionesBase.get(estacionId))
    .filter(Boolean)
);

const selectEstacion = (est: IEstacion) => {
  estacion.value = est;
  state.show = false;

  emit('estacion', estacion.value);
};

const controller = new AbortController();

onMounted(() => {
  window.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.mb-estacion__select-container')) {
        return;
      }

      state.show = false;
    },
    { signal: controller.signal }
  );
});
onBeforeUnmount(() => {
  controller.abort();
});
</script>
<style scoped>
.slide-open-enter-active,
.slide-open-leave-active {
  transition: height 0.2s ease-in-out;
  will-change: height;
}
.slide-open-enter-from,
.slide-open-leave-to {
  height: 0;
}
.slide-open-enter-active li,
.slide-open-leave-active li {
  transition: all 0.2s ease-in-out;
  transition-delay: calc(var(--i) * 0.055s);
  /* will-change: opacity,transform; */
}
.slide-open-enter-from li,
.slide-open-leave-to li {
  opacity: 0;
  transform: translateY(-50%);
}
</style>
